import { Injectable } from '@angular/core';
import { DataType, ExprCall, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { EncField, Encoding, EncodingResult, ModifyField, VariableType } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, FormulaOperations, Switch } from '@mode/shared/util-switch';

@Injectable()
export abstract class UpdateFieldService {
  constructor(public chartFacade: ChartFacade) {}

  abstract updateFieldEncoding(
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    originalField: EncField,
    modifiedField: ModifyField
  ): EncodingResult;

  abstract makeFieldFormatsForTableCalc(
    originalField: EncField,
    formula: CompiledField,
    encoding: Encoding
  ): ChartTypes.FieldFormatMap | undefined;

  abstract makeFieldFormatsForTrend(
    trend: ExprForm | null,
    encoding: Encoding,
    field: EncField,
    view: FlamingoTypes.ModeView
  ): { encResult: EncodingResult; fieldFormats: ChartTypes.FieldFormatMap | undefined } | undefined;

  abstract clearFrame(encoding: Encoding, field: EncField): Encoding;

  abstract selectFieldToFormat(field: EncField): void;

  getAttrType(model: FlamingoTypes.ModeModel, view: FlamingoTypes.ModeView, attrName: string): VariableType {
    return Switch.Compat.makeVariableType(this.getAttr(model, view, attrName).type);
  }

  getAttr(model: FlamingoTypes.ModeModel, view: FlamingoTypes.ModeView, attrName: string): FlamingoTypes.ModeAttr {
    const attr = Switch.Attrs.find(model, view.name, attrName);
    if (attr == null) {
      throw new Error(
        `Error in UpdateFieldService#setAggregate: Could not locate field with name ` +
          `"${attrName}" in view "${view.name}"`
      );
    } else {
      return attr;
    }
  }

  updateAggregate(
    aggregate: ExprCall.Name | null,
    field: EncField,
    formula: CompiledField,
    model: FlamingoTypes.ModeModel,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    allowContinuous: boolean,
    allowDiscrete: boolean
  ) {
    // Bail if
    // ---
    // 1. The formula doesn't expose an attrName
    // 2. The component field or field id is empty

    if (formula?.attrName == null || field?.id == null) {
      return;
    }

    const isMinOrMax = (name: string | undefined): name is ExprCall.Name.Min | ExprCall.Name.Max => {
      if (typeof name === 'string') {
        switch (name.toUpperCase()) {
          case ExprCall.Name.Min:
          case ExprCall.Name.Max: {
            return true;
          }
        }
      }
      return false;
    };

    let source: string | undefined;
    let varType: VariableType | undefined;

    let updatedEncoding = encoding;

    // Unsetting aggregation
    if (aggregate == null) {
      // Access expression of the underlying attr
      source = FormulaOperations.access(formula);

      // VariableType is the original attr type if that type is allowed.
      // Otherwise, use the existing formula type.
      const attrVarType = this.getAttrType(model, view, formula.attrName);
      varType =
        (attrVarType === VariableType.CONTINUOUS && allowContinuous) ||
        (attrVarType === VariableType.DISCRETE && allowDiscrete)
          ? attrVarType
          : formula.varType === Attr.Type.Continuous
            ? VariableType.CONTINUOUS
            : VariableType.DISCRETE;

      // If it's a known analytic form, clear the frame
      if (formula.hasAnalyticFunc) {
        updatedEncoding = this.clearFrame(encoding, field);
      }
    }
    // Setting or changing the aggregation
    // ---
    // Jump through some hoops..
    else if (formula.isAggregateable || formula.hasAggFunc) {
      // Aggregate expression of the underlying attr
      source = FormulaOperations.aggregate(formula, aggregate);

      // If this is a known form quick calc then we
      // are changing the underlying aggregation of
      // the analytic expression
      switch (formula.form) {
        case ExprForm.Difference: {
          source = FormulaOperations.diff(formula, aggregate);
          break;
        }
        case ExprForm.PctDifference: {
          source = FormulaOperations.pctDiff(formula, aggregate);
          break;
        }
        case ExprForm.PctOfTotal: {
          source = FormulaOperations.pctOfTotal(formula, aggregate);
          break;
        }
      }

      // First time setting an aggregation is _tentatively_
      // gonna produce a continuous formula.. we refine this
      // below when checking the agg function and attr type
      varType = VariableType.CONTINUOUS;

      // Updating the aggregation is _tentatively_ going to produce
      // a formula of the same varType.. we also refine this below
      // when checking the agg function and attr type
      if (formula.hasAggFunc) {
        varType = Switch.Compat.makeVariableType(formula.varType);
      }

      const fromMinOrMax = isMinOrMax(formula.aggFunc);
      const toMinOrMax = isMinOrMax(aggregate);

      // Transitioning from MIN|MAX -> some other aggregate
      if (fromMinOrMax && !toMinOrMax) {
        const attr = this.getAttr(model, view, formula.attrName);
        switch (attr.formula.type.name) {
          case DataType.Name.String:
          case DataType.Name.Boolean: {
            // MIN|MAX of String|Boolean _was_ discrete by necessity.
            // Since we're switching to a different agg this may now
            // be continuous
            varType = VariableType.CONTINUOUS;
            break;
          }
        }
      }
      // Transitioning from some aggregate -> MIN|MAX
      else if (!fromMinOrMax && toMinOrMax) {
        const attr = this.getAttr(model, view, formula.attrName);
        switch (attr.formula.type.name) {
          case DataType.Name.String:
          case DataType.Name.Boolean: {
            // MIN|MAX of String|Boolean must be discrete
            varType = VariableType.DISCRETE;
            break;
          }
        }
      }
    }

    if (source != null && varType != null) {
      // Ensures rows/cols in PTQC are discrete
      const updatedVarType =
        varType === VariableType.CONTINUOUS && allowContinuous === false ? VariableType.DISCRETE : varType;

      return this.updateFieldEncoding(view, updatedEncoding, field, {
        id: field.id,
        formula: source,
        variable: {
          type: updatedVarType,
        },
      });
    }
  }

  updateVariableType(type: VariableType, view: FlamingoTypes.ModeView, encoding: Encoding, field: EncField) {
    return this.updateFieldEncoding(view, encoding, field, {
      id: field.id ?? '',
      variable: {
        type: type,
      },
    });
  }

  updateTrend(
    trend: ExprForm | null,
    field: EncField,
    formula: CompiledField,
    view: FlamingoTypes.ModeView,
    encoding: Encoding
  ) {
    const fieldId = field.id;

    if (fieldId == null) {
      return;
    }

    let source: string | undefined;
    let updatedEncoding = encoding;

    switch (trend) {
      case ExprForm.Difference: {
        source = FormulaOperations.diff(formula);
        break;
      }
      case ExprForm.PctDifference: {
        source = FormulaOperations.pctDiff(formula);
        break;
      }
      case ExprForm.PctOfTotal: {
        source = FormulaOperations.pctOfTotal(formula);
        break;
      }
      default: {
        source = FormulaOperations.clearAnalytic(formula);
        updatedEncoding = this.clearFrame(updatedEncoding, field);
        break;
      }
    }

    if (typeof source === 'string') {
      // Update the encoding with the new source formula
      let encResult = this.updateFieldEncoding(view, updatedEncoding, field, {
        id: fieldId,
        formula: source,
      });
      return this.makeFieldFormatsForTrend(trend, encResult.encoding, field, view);
    }
  }

  updateTableCalcSettings(
    originalField: EncField,
    formula: CompiledField,
    view: FlamingoTypes.ModeView | undefined,
    encoding: Encoding
  ) {
    if (view == null) {
      return;
    }
    const modifyField = formula.toModifyField();
    const encResult = this.updateFieldEncoding(view, encoding, originalField, modifyField);

    // Set the format to percent if the formula was _changed_ to a Pct* formula
    const fieldFormats = this.makeFieldFormatsForTableCalc(originalField, formula, encResult.encoding);
    return { encResult, fieldFormats };
  }
}
